/**
 * iframe messaging utility for TwoPoint widget
 * Handles cross-origin communication for automatic height adjustment
 */

export interface ParentInitMessage {
  type: 'TWIPOINT_PARENT_INIT';
  parentOrigin: string;
  widgetId?: string;
}

export interface HeightUpdateMessage {
  type: 'TWIPOINT_IFRAME_HEIGHT';
  height: number;
  widgetId?: string;
}

export interface ScrollToTopMessage {
  type: 'TWIPOINT_SCROLL_TO_TOP';
  widgetId?: string;
}

const isParentInitMessage = (data: unknown): data is ParentInitMessage => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'type' in data &&
    data.type === 'TWIPOINT_PARENT_INIT' &&
    'parentOrigin' in data &&
    typeof (data as ParentInitMessage).parentOrigin === 'string'
  );
};

const isDebugMode = (): boolean => {
  const urlParams = new URLSearchParams(window.location.search);
  return urlParams.get('twopointDebug') === '1' ||
         localStorage.getItem('twopointDebug') === '1';
};

/**
 * Calculate content height with buffer
 *
 * Uses the last child's bottom position instead of scrollHeight to avoid
 * circular dependency where parent iframe height affects element's scrollHeight.
 */
export const calculateContentHeight = (element: HTMLElement | null): number => {
  if (!element) {
    return 0;
  }


  // Get all children
  const children = Array.from(element.children) as HTMLElement[];

  if (children.length === 0) {
    return element.scrollHeight + 50;
  }

  // Find the bottom-most child
  let maxBottom = 0;
  let bottomMostChildClass = '';
  children.forEach(child => {
    const rect = child.getBoundingClientRect();
    const parentRect = element.getBoundingClientRect();
    const relativeBottom = rect.bottom - parentRect.top;
    if (relativeBottom > maxBottom) {
      maxBottom = relativeBottom;
      bottomMostChildClass = child.className || child.tagName;
    }
  });

  const BUFFER = 16;
  const height = Math.ceil(maxBottom) + BUFFER;

  if (isDebugMode()) {
    console.log('[TwoPoint iframe] Height calculated', {
      maxBottom: Math.ceil(maxBottom),
      buffer: BUFFER,
      final: height,
      childrenCount: children.length,
      bottomChild: bottomMostChildClass,
      currentPath: window.location.pathname
    });
  }

  return height;
};

/**
 * Send height to parent window via postMessage
 */
export const sendHeightToParent = (
  height: number,
  parentOrigin: string,
  widgetId?: string | null
): void => {
  if (!parentOrigin) {
    console.error('[TwoPoint iframe] Cannot send height - no parentOrigin');
    return;
  }

  const message: HeightUpdateMessage = {
    type: 'TWIPOINT_IFRAME_HEIGHT',
    height: height
  };

  if (widgetId) {
    message.widgetId = widgetId;
  }

  const targetOrigin = parentOrigin === 'file://' ? '*' : parentOrigin;

  if (isDebugMode()) {
    console.log('[TwoPoint iframe] Sending height', { height, widgetId, targetOrigin });
  }

  try {
    window.parent.postMessage(message, targetOrigin);
  } catch (error) {
    console.error('[TwoPoint iframe] Failed to send message:', error);
  }
};

/**
 * Send scroll to top request to parent window
 */
export const sendScrollToTop = (
  parentOrigin: string,
  widgetId?: string | null
): void => {
  if (!parentOrigin) {
    console.error('[TwoPoint iframe] Cannot send scroll request - no parentOrigin');
    return;
  }

  const message: ScrollToTopMessage = {
    type: 'TWIPOINT_SCROLL_TO_TOP'
  };

  if (widgetId) {
    message.widgetId = widgetId;
  }

  const targetOrigin = parentOrigin === 'file://' ? '*' : parentOrigin;

  if (isDebugMode()) {
    console.log('[TwoPoint iframe] Requesting scroll to top', { widgetId, targetOrigin });
  }

  try {
    window.parent.postMessage(message, targetOrigin);
  } catch (error) {
    console.error('[TwoPoint iframe] Failed to send scroll request:', error);
  }
};

// Canonical type: TWOPOINT_*. Legacy TWIPOINT_* (typo) kept for backward compat —
// both are emitted by sendPropertySelected so existing hosts keep working.
export type PropertySelectedType = 'TWOPOINT_PROPERTY_SELECTED' | 'TWIPOINT_PROPERTY_SELECTED';

export interface PropertySelectedMessage {
  type: PropertySelectedType;
  propertyId: string;
  widgetId?: string;
}

// Module-level store — populated after handshake
let _parentOrigin: string | null = null;
let _widgetId: string | null = null;

export const registerParentConnection = (parentOrigin: string, widgetId: string | null): void => {
  _parentOrigin = parentOrigin;
  _widgetId = widgetId;
};

export const getParentOrigin = (): string | null => {
  if (_parentOrigin) return _parentOrigin;
  // Fallback: derive from document.referrer when running in iframe without handshake
  if (window.self !== window.top && document.referrer) {
    try {
      const url = new URL(document.referrer);
      // Return full URL without query/hash so deep link can append ?property=
      return url.origin + url.pathname;
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Send property selection event to parent window
 */
// Emitted types, canonical first. Legacy TWIPOINT_* retained for backward compat.
const PROPERTY_SELECTED_TYPES: PropertySelectedType[] = [
  'TWOPOINT_PROPERTY_SELECTED',
  'TWIPOINT_PROPERTY_SELECTED'
];

export const sendPropertySelected = (propertyId: string): void => {
  if (!_parentOrigin) return;

  const targetOrigin = _parentOrigin === 'file://' ? '*' : _parentOrigin;

  for (const type of PROPERTY_SELECTED_TYPES) {
    const message: PropertySelectedMessage = { type, propertyId };
    if (_widgetId) {
      message.widgetId = _widgetId;
    }
    try {
      window.parent.postMessage(message, targetOrigin);
    } catch (error) {
      console.error('[TwoPoint iframe] Failed to send property selected:', error);
    }
  }
};

export type HandshakeCallback = (parentOrigin: string, widgetId: string | null) => void;

/**
 * Setup handshake listener for parent initialization
 * Returns function to remove the listener
 */
export const setupHandshakeListener = (onHandshakeComplete: HandshakeCallback): (() => void) => {
  const handleMessage = (event: MessageEvent): void => {
    if (!isParentInitMessage(event.data)) return;

    const { parentOrigin, widgetId } = event.data;

    if (isDebugMode()) {
      console.log('[TwoPoint iframe] Handshake received', { parentOrigin, widgetId });
    }

    onHandshakeComplete(parentOrigin, widgetId || null);
  };

  window.addEventListener('message', handleMessage);

  return () => window.removeEventListener('message', handleMessage);
};
